import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Analytics } from '../types';
import { CATEGORY_META } from '../types';
import { useCategories } from './useCategories';
import { isPremiumPlan } from '../lib/plans';

export function useAnalytics() {
  const { user, session, profile } = useAuth();
  useCategories(); // ensure fetched or preloaded
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fallbackAnalytics = useCallback(async (): Promise<Analytics> => {
    if (!user) {
      return {
        totalSpent: 0,
        currentMonthSpent: 0,
        previousMonthSpent: 0,
        monthlyChange: 0,
        recurringTotal: 0,
        topCategory: null,
        byCategory: [],
        byMonth: [],
        expenseCount: 0,
        premiumVisuals: null,
      };
    }

    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 11);
    const from = fromDate.toISOString().slice(0, 10);

    const { data, error: dbError } = await supabase
      .from('expenses')
      .select('amount, category, date, recurring')
      .eq('user_id', user.id)
      .gte('date', from);

    if (dbError) throw dbError;

    const rows = (data ?? []) as Array<{ amount: number; category: string; date: string; recurring: boolean }>;

    const monthMap = new Map<string, { total: number; count: number }>();
    const categoryMap = new Map<string, { total: number; count: number }>();
    let recurringTotal = 0;

    for (const row of rows) {
      const month = row.date.slice(0, 7);
      const m = monthMap.get(month) ?? { total: 0, count: 0 };
      m.total += Number(row.amount);
      m.count += 1;
      monthMap.set(month, m);

      const c = categoryMap.get(row.category) ?? { total: 0, count: 0 };
      c.total += Number(row.amount);
      c.count += 1;
      categoryMap.set(row.category, c);

      if (row.recurring) recurringTotal += Number(row.amount);
    }

    const now = new Date();
    const labels = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    const byMonth = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const m = monthMap.get(key) ?? { total: 0, count: 0 };
      return {
        month: key,
        label: `${labels[d.getMonth()]} ${d.getFullYear()}`,
        total: Number(m.total.toFixed(2)),
        count: m.count,
      };
    });

    const totalSpent = byMonth.reduce((sum, m) => sum + m.total, 0);
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthKey = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;

    const currentMonthSpent = byMonth.find(m => m.month === currentMonthKey)?.total ?? 0;
    const previousMonthSpent = byMonth.find(m => m.month === prevMonthKey)?.total ?? 0;
    const monthlyChange = previousMonthSpent === 0 ? 100 : ((currentMonthSpent - previousMonthSpent) / previousMonthSpent) * 100;

    const byCategory = Array.from(categoryMap.entries())
      .map(([category, value]) => ({
        category,
        total: Number(value.total.toFixed(2)),
        count: value.count,
        percentage: totalSpent > 0 ? (value.total / totalSpent) * 100 : 0,
      }))
      .sort((a, b) => b.total - a.total);

    const premiumVisuals = isPremiumPlan(profile?.plan)
      ? {
          sankey: {
            nodes: [
              { name: 'Entrate' },
              { name: 'Budget' },
              ...byCategory.map(c => ({ name: c.category })),
            ],
            links: [
              {
                source: 0,
                target: 1,
                value: Number(totalSpent.toFixed(2)),
                category: 'income',
              },
              ...byCategory.map((c, i) => ({
                source: 1,
                target: i + 2,
                value: Number(c.total.toFixed(2)),
                category: c.category,
              })),
            ],
          },
          savingsLine: byMonth.map(m => ({
            month: m.month,
            label: m.label,
            income: 0,
            spent: Number(m.total.toFixed(2)),
            saving: Number((-m.total).toFixed(2)),
          })),
        }
      : null;

    return {
      totalSpent: Number(totalSpent.toFixed(2)),
      currentMonthSpent: Number(currentMonthSpent.toFixed(2)),
      previousMonthSpent: Number(previousMonthSpent.toFixed(2)),
      monthlyChange,
      recurringTotal: Number(recurringTotal.toFixed(2)),
      topCategory: byCategory[0]?.category ?? null,
      byCategory,
      byMonth,
      expenseCount: rows.length,
      premiumVisuals,
    };
  }, [user, profile?.plan]);

  const fetchAnalytics = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-analytics', {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (fnError) {
        const fallback = await fallbackAnalytics();
        setAnalytics(fallback);
        setError(null);
        return;
      }
      setAnalytics(data as Analytics);
    } catch (err) {
      try {
        const fallback = await fallbackAnalytics();
        setAnalytics(fallback);
        setError(null);
      } catch (fallbackErr) {
        setError(fallbackErr instanceof Error ? fallbackErr.message : 'Failed to fetch analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [user, session, fallbackAnalytics]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // Keep dashboard analytics in sync when expenses change
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`analytics:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expenses', filter: `user_id=eq.${user.id}` },
        () => { fetchAnalytics(); },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchAnalytics]);

  return { analytics, loading, error, refetch: fetchAnalytics };
}

// Re-export for convenience with custom resolver
export function getCategoryMeta(cat: string, customCats: {name: string, emoji: string}[] = []) {
  const normCat = cat.toLowerCase();
  const toTitleCase = (s: string) => s
    .trim()
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
  
  if (CATEGORY_META[cat as keyof typeof CATEGORY_META]) {
      return CATEGORY_META[cat as keyof typeof CATEGORY_META];
  }
  
  const found = customCats.find(c => c.name.toLowerCase() === normCat);
  if (found) {
      return { label: toTitleCase(found.name), icon: found.emoji, color: '#facc15', bgColor: 'rgba(250,204,21,0.15)' };
  }
  
    return { label: toTitleCase(cat), icon: '🏷️', color: '#cbd5e1', bgColor: 'rgba(203,213,225,0.15)' };
}
